import React from 'react';
import './FlashcardViewer.css';

/**
 * FlashcardViewer - Premium card viewing experience for Study tab
 * 
 * Features:
 * - Large centered card with FRONT/BACK label
 * - Progress row with viewed/flipped chips
 * - Info bar with marked/known/viewed counts (expanded mode only)
 * - Bottom action bar with navigation and learning actions
 * - Keyboard shortcuts help button
 * 
 * @param {Object} props
 * @param {Object} props.card - Current flashcard {front_text, back_text, flashcard_id}
 * @param {boolean} props.isFlipped - Whether card is flipped to back
 * @param {Function} props.onFlip - Toggle flip handler
 * @param {number} props.currentIndex - Current card index (0-based)
 * @param {number} props.totalCards - Total number of cards in current view
 * @param {number} props.viewedCount - Number of viewed cards
 * @param {number} props.flippedCount - Number of flipped cards
 * @param {number} props.markedCount - Number of marked cards
 * @param {number} props.knownCount - Number of known cards
 * @param {boolean} props.isMarked - Whether current card is marked
 * @param {boolean} props.isKnown - Whether current card is known
 * @param {Function} props.onPrevious - Previous card handler
 * @param {Function} props.onNext - Next card handler
 * @param {Function} props.onShuffle - Shuffle deck handler
 * @param {Function} props.onToggleMarked - Toggle marked status handler
 * @param {Function} props.onToggleKnown - Toggle known status handler
 * @param {Function} props.onShowShortcuts - Show shortcuts modal handler
 * @param {boolean} props.isExpanded - Whether panel is expanded (shows info bar)
 * @param {boolean} props.isShuffled - Whether deck is currently shuffled
 * @param {Function} props.onResetOrder - Reset to original order handler
 * @param {boolean} props.reviewMode - Review mode: null | 'marked_review'
 * @param {Function} props.onExitReview - Exit review mode handler
 * @param {boolean} props.isShuffling - Whether shuffle animation is active
 * @param {Function} props.onEnterReview - Enter marked review mode handler
 */
export default function FlashcardViewer({
  card,
  isFlipped,
  onFlip,
  currentIndex,
  totalCards,
  viewedCount,
  flippedCount,
  markedCount,
  knownCount,
  isMarked,
  isKnown,
  onPrevious,
  onNext,
  onShuffle,
  onToggleMarked,
  onToggleKnown,
  onShowShortcuts,
  isExpanded = false,
  isShuffled = false,
  onResetOrder,
  reviewMode = null,
  onExitReview,
  isShuffling = false,
  onEnterReview
}) {
  // Debug: log card structure
  if (card && currentIndex === 0) {
    console.log('[FlashcardViewer] Card structure:', card);
    console.log('[FlashcardViewer] Card properties:', Object.keys(card));
    console.log('[FlashcardViewer] Front text:', card?.front_text, card?.frontText, card?.front);
    console.log('[FlashcardViewer] Back text:', card?.back_text, card?.backText, card?.back);
  }
  
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < totalCards - 1;
  
  // Progress percentage
  const _progressPercent = totalCards > 0 ? ((currentIndex + 1) / totalCards) * 100 : 0; // eslint-disable-line no-unused-vars

  return (
    <div className="flashcard-viewer">
      {/* Review mode pill */}
      {reviewMode === 'marked_review' && (
        <div className="flashcard-viewer__review-pill">
          <span>Reviewing: Marked</span>
          <button
            className="flashcard-viewer__review-exit"
            onClick={onExitReview}
            aria-label="Exit review mode"
          >
            ✕
          </button>
        </div>
      )}

      {/* Info bar (expanded mode only) */}
      {isExpanded && (markedCount > 0 || knownCount > 0) && (
        <div className="flashcard-viewer__info-bar">
          {markedCount > 0 && <span>Marked: {markedCount}</span>}
          {knownCount > 0 && <span>Known: {knownCount}</span>}
          {markedCount > 0 && reviewMode !== 'marked_review' && onEnterReview && (
            <>
              <span className="flashcard-viewer__info-separator">•</span>
              <button 
                className="flashcard-viewer__info-link"
                onClick={onEnterReview}
              >
                Review Marked
              </button>
            </>
          )}
        </div>
      )}

      {/* Card with side navigation arrows */}
      <div className="flashcard-viewer__card-row">
        {/* Left arrow */}
        <button
          className="flashcard-viewer__side-arrow flashcard-viewer__side-arrow--left"
          onClick={onPrevious}
          disabled={!canGoPrevious}
          aria-label="Previous card"
          title="Previous card"
        >
          ‹
        </button>

        <div 
          className={`flashcard-viewer__card-container ${isShuffling ? 'flashcard-viewer__card-container--shuffling' : ''}`}
        >
          {/* Stacked deck layers */}
          <div className="flashcard-viewer__deck-stack">
            <div className="flashcard-viewer__deck-layer flashcard-viewer__deck-layer--2" />
            <div className="flashcard-viewer__deck-layer flashcard-viewer__deck-layer--1" />
          <div
            className="flashcard-viewer__card"
            onClick={onFlip}
            role="button"
            tabIndex={0}
            aria-label={isFlipped ? "Flip card to front" : "Flip card to back"}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                onFlip();
              }
            }}
          >
            <div className={`flashcard-viewer__card-inner ${isFlipped ? 'flashcard-viewer__card-inner--flipped' : ''}`}>
              {/* Front face */}
              <div className="flashcard-viewer__card-face flashcard-viewer__card-face--front">
                <div className="flashcard-viewer__card-label">FRONT</div>
                <button
                  className={`flashcard-viewer__card-flag ${isMarked ? 'flashcard-viewer__card-flag--active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleMarked();
                  }}
                  title={isMarked ? 'Unflag card' : 'Flag card for review'}
                  aria-label={isMarked ? 'Unflag card' : 'Flag card'}
                >
                  ⚑
                </button>
                <div className="flashcard-viewer__card-text">
                  {card?.front_text || card?.frontText || 'No content'}
                </div>
                <div className="flashcard-viewer__card-hint">
                  Click or press Space to flip
                </div>
              </div>

              {/* Back face */}
              <div className="flashcard-viewer__card-face flashcard-viewer__card-face--back">
                <div className="flashcard-viewer__card-label">BACK</div>
                <button
                  className={`flashcard-viewer__card-flag ${isMarked ? 'flashcard-viewer__card-flag--active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleMarked();
                  }}
                  title={isMarked ? 'Unflag card' : 'Flag card for review'}
                  aria-label={isMarked ? 'Unflag card' : 'Flag card'}
                >
                  ⚑
                </button>
                <div className="flashcard-viewer__card-text">
                  {card?.back_text || card?.backText || 'No content'}
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Right arrow */}
        <button
          className="flashcard-viewer__side-arrow flashcard-viewer__side-arrow--right"
          onClick={onNext}
          disabled={!canGoNext}
          aria-label="Next card"
          title="Next card"
        >
          ›
        </button>
      </div>

      {/* Utility controls row: Flip, Shuffle, Shortcuts */}
      <div className="flashcard-viewer__utility-row">
        <button
          className="ui-btn ui-btn--ghost flashcard-viewer__icon-btn"
          onClick={onFlip}
          aria-label="Flip card"
          title="Flip card"
        >
          ↻
        </button>
        {isShuffled ? (
          <button
            className="ui-btn ui-btn--ghost flashcard-viewer__icon-btn"
            onClick={onResetOrder}
            aria-label="Reset order"
            title="Reset to original order"
          >
            ↺
          </button>
        ) : (
          <button
            className="ui-btn ui-btn--ghost flashcard-viewer__icon-btn"
            onClick={onShuffle}
            aria-label="Shuffle"
            title="Shuffle cards"
          >
            🔀
          </button>
        )}
        <button
          className="ui-btn ui-btn--ghost flashcard-viewer__icon-btn flashcard-viewer__shortcuts-btn"
          onClick={onShowShortcuts}
          title="Shortcuts"
          aria-label="Show keyboard shortcuts"
        >
          ?
        </button>
      </div>

      {/* Learning feedback row */}
      <div className="flashcard-viewer__learning-actions">
        <button
          className={`flashcard-viewer__known-btn flashcard-viewer__known-btn--miss-style ${isKnown === false ? 'flashcard-viewer__known-btn--flash-miss' : ''}`}
          onClick={() => onToggleKnown('miss')}
        >
          ✕ Didn't Know
        </button>
        <button
          className={`flashcard-viewer__known-btn flashcard-viewer__known-btn--hit-style ${isKnown === true ? 'flashcard-viewer__known-btn--flash-hit' : ''}`}
          onClick={() => onToggleKnown('hit')}
        >
          ✓ I Knew It
        </button>
      </div>
    </div>
  );
}
